export const isFileImage = (file: File) => file.type.startsWith('image/');
export const isAudioFile = (file: File) => file.type.startsWith('audio/');
export const isTextFile = (file: File) => file.type.startsWith('text/');
export const isVideoFile = (file: File) => file.type.startsWith('video/');
export const isPDF = (f: File) => f.type === 'application/pdf';

export const MAX_FILES = 3;
export const MAX_TOTAL_MB = 70;
export const MAX_TOTAL_BYTES = MAX_TOTAL_MB * 1024 * 1024;

export const formatMB = (bytes: number) => (bytes / (1024 * 1024)).toFixed(2) + 'MB';

export const truncate = (s: string, n = 50) => (s.length > n ? s.slice(0, n) + '…' : s);

export const ACCEPTED_FILE_TYPES = 'image/*,audio/*,text/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv';

export const dedupe = (arr: File[]) => {
  const mapping = new Map<string, File>();

  for (const element of arr) {
    const format = `${element.name}|${element.size}|${element.lastModified}`;
    if (!mapping.has(format)) mapping.set(format, element);
  }

  return Array.from(mapping.values());
};

export const flattenArray = (channelData: Float32Array[]): Float32Array => {
  const length = channelData.reduce((acc, arr) => acc + arr.length, 0);
  const result = new Float32Array(length);
  let offset = 0;

  for (const arr of channelData) {
    result.set(arr, offset);
    offset += arr.length;
  }

  return result;
};

const writeString = (view: DataView, offset: number, str: string) => {
  for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
};

export const encodeWavFloat32 = (samples: Float32Array, sampleRate: number): ArrayBuffer => {
  const buffer = new ArrayBuffer(44 + samples.length * 4);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 4, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 3, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 4, true);
  view.setUint16(32, 4, true);
  view.setUint16(34, 32, true);
  writeString(view, 36, 'data');
  view.setUint32(40, samples.length * 4, true);

  let offset = 44;

  for (let i = 0; i < samples.length; i++, offset += 4) {
    view.setFloat32(offset, samples[i], true);
  }

  return buffer;
};

export const encodeWav = (samples: Float32Array, sampleRate: number): ArrayBuffer => {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;

  for (let i = 0; i < samples.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return buffer;
};
